import type { SyncStatus } from '../shared/types'
import { checkAlive, untilAborted, withTimeout } from '../utils/abort'
import { createLogger } from '../utils/logger'

const log = createLogger('SyncWorkerHealthMonitor')

const HEARTBEAT_INTERVAL_MS = 15000
const HEARTBEAT_TIMEOUT_MS = 30000
const CRASH_RESET_WINDOW_MS = 60000
export const MAX_CONSECUTIVE_TIMEOUTS = 5
export const MAX_CONSECUTIVE_CRASHES = 3
const DEFAULT_MAX_MISSED_PINGS = 2

/** The worker didn't answer a ping in time */
export class HeartbeatTimeoutError extends Error {
  constructor() {
    super('Heartbeat timeout')
    this.name = 'HeartbeatTimeoutError'
  }
}

export interface SendPingOptions {
  signal?: AbortSignal
  timeoutMs?: number
}

export interface HealthCheckOptions {
  worker: Worker
  pingPort: MessagePort
  isCurrentWorker: () => boolean
  onCrash: (willRestart?: boolean) => void
  onRestart: () => void
  heartbeatIntervalMs?: number
  heartbeatTimeoutMs?: number
  maxMissedPings?: number
}

interface CrashDetails {
  worker: Worker
  onCrash: (willRestart?: boolean) => void
  onRestart: () => void
  isTimeout?: boolean
}

export const sendPing = async (
  port: MessagePort,
  { signal, timeoutMs }: SendPingOptions = {},
): Promise<void> => {
  checkAlive(signal, true, 'Ping aborted')

  let handleMessage!: (event: MessageEvent) => void
  let handleMessageError!: () => void
  const pong = new Promise<void>((resolve, reject) => {
    handleMessage = event => {
      if (event.data === 'pong') resolve()
    }
    handleMessageError = () => reject(new Error('MessagePort error'))
  })
  const untilPong = (pingSignal?: AbortSignal) => (pingSignal ? untilAborted(pong, pingSignal) : pong)

  port.addEventListener('message', handleMessage)
  port.addEventListener('messageerror', handleMessageError)
  try {
    port.start()
    port.postMessage('ping')
    await (timeoutMs !== undefined && timeoutMs > 0
      ? withTimeout(timeoutMs, untilPong, { signal, reason: new HeartbeatTimeoutError() })
      : untilPong(signal))
  } finally {
    port.removeEventListener('message', handleMessage)
    port.removeEventListener('messageerror', handleMessageError)
  }
}

function isExternalAbort(signal: AbortSignal): boolean {
  return signal.aborted && !(signal.reason instanceof HeartbeatTimeoutError)
}

/** Where the health monitor reports problems with the sync worker */
export interface WorkerHealthReporter {
  setSyncStatus(status: SyncStatus): void
  /** null clears the warning */
  setSyncWarning(message: string | null): void
  setFatalError(message: string): void
}

const noopReporter: WorkerHealthReporter = {
  setSyncStatus: () => {},
  setSyncWarning: () => {},
  setFatalError: () => {},
}

export class SyncWorkerHealthMonitor {
  constructor(private readonly reporter: WorkerHealthReporter = noopReporter) {}

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private crashCount = 0
  private timeoutCrashCount = 0
  private lastCrashTime = 0
  private isPinging = false
  private activePingAbortController: AbortController | null = null
  private cleanupCurrentWorkerListeners: (() => void) | null = null
  private lastWorkerActivityTime = 0
  private consecutiveMissedPings = 0
  private lastTickTime = 0

  recordActivity = (): void => {
    this.lastWorkerActivityTime = Date.now()
    if (this.consecutiveMissedPings > 0) {
      this.consecutiveMissedPings = 0
      this.reporter.setSyncWarning(null)
    }
  }

  stopWorkerHeartbeat = (): void => {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
    if (this.activePingAbortController !== null) {
      this.activePingAbortController.abort(new Error('Heartbeat stopped'))
      this.activePingAbortController = null
    }
    if (this.cleanupCurrentWorkerListeners !== null) {
      this.cleanupCurrentWorkerListeners()
      this.cleanupCurrentWorkerListeners = null
    }
    this.isPinging = false
    this.consecutiveMissedPings = 0
  }

  resetCrashMetrics = (): void => {
    this.crashCount = 0
    this.timeoutCrashCount = 0
    this.lastCrashTime = 0
    this.consecutiveMissedPings = 0
    this.lastWorkerActivityTime = Date.now()
    this.lastTickTime = 0
  }

  handleWorkerCrash({
    worker,
    onCrash,
    onRestart,
    isTimeout = false,
  }: CrashDetails): void {
    this.stopWorkerHeartbeat()

    try {
      worker.terminate()
    } catch (err) {
      log.error('Error terminating crashed worker:', err)
    }

    const now = Date.now()
    if (now - this.lastCrashTime > CRASH_RESET_WINDOW_MS) {
      this.crashCount = 1
      this.timeoutCrashCount = isTimeout ? 1 : 0
    } else {
      this.crashCount += 1
      if (isTimeout) {
        this.timeoutCrashCount += 1
      }
    }
    this.lastCrashTime = now

    const maxAllowed = isTimeout ? MAX_CONSECUTIVE_TIMEOUTS : MAX_CONSECUTIVE_CRASHES
    const currentCount = isTimeout ? this.timeoutCrashCount : this.crashCount
    const willRestart = currentCount < maxAllowed

    onCrash(willRestart)

    if (!willRestart) {
      log.error(
        `Worker halted after ${currentCount} consecutive ${isTimeout ? 'timeouts' : 'crashes'}. Halting auto-restart.`
      )
      const errorMsg = isTimeout
        ? 'Sync worker became unresponsive repeatedly. Please refresh the page to try again.'
        : 'Sync worker crashed repeatedly. Please refresh the page to try again.'
      this.reporter.setFatalError(errorMsg)
      this.reporter.setSyncStatus('dead')
    } else {
      log.warn(
        `Attempting automatic restart (${isTimeout ? 'timeout' : 'crash'} count: ${currentCount}/${maxAllowed})...`
      )
      this.reporter.setSyncStatus('connecting')
      this.reporter.setSyncWarning('Sync connection lost. Reconnecting...')
      onRestart()
    }
  }

  setupWorkerHealthCheck = ({
    worker,
    pingPort,
    isCurrentWorker,
    onCrash,
    onRestart,
    heartbeatIntervalMs = HEARTBEAT_INTERVAL_MS,
    heartbeatTimeoutMs = HEARTBEAT_TIMEOUT_MS,
    maxMissedPings = DEFAULT_MAX_MISSED_PINGS,
  }: HealthCheckOptions): void => {
    this.stopWorkerHeartbeat()

    const handleCrash = (isTimeout = false) => {
      if (!isCurrentWorker()) return
      this.handleWorkerCrash({ worker, onCrash, onRestart, isTimeout })
    }

    const errorHandler = (description: string) => (event: Event) => {
      log.error(`Web worker ${description}:`, event)
      handleCrash(false)
    }
    const handleError = errorHandler('error')
    const handleMsgError = errorHandler('message error')

    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        this.lastTickTime = Date.now()
      }
    }

    worker.addEventListener('error', handleError)
    worker.addEventListener('messageerror', handleMsgError)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }

    this.cleanupCurrentWorkerListeners = () => {
      worker.removeEventListener('error', handleError)
      worker.removeEventListener('messageerror', handleMsgError)
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }

    this.lastTickTime = Date.now()
    this.lastWorkerActivityTime = Date.now()
    this.consecutiveMissedPings = 0

    this.heartbeatTimer = setInterval(async () => {
      if (!isCurrentWorker()) {
        this.stopWorkerHeartbeat()
        return
      }

      // 1. Tab visibility: skip heartbeat initiation if tab is backgrounded/hidden
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return
      }

      // 2. Sleep-wake / clock jump detection: if interval was delayed significantly, system slept or was throttled
      const now = Date.now()
      const timeSinceLastTick = now - this.lastTickTime
      this.lastTickTime = now

      if (timeSinceLastTick > heartbeatIntervalMs * 2.5) {
        log.warn(`Sleep/throttle detected (${timeSinceLastTick}ms elapsed since last tick). Discarding stale ping.`)
        if (this.isPinging && this.activePingAbortController) {
          this.activePingAbortController.abort(new Error('System sleep detected'))
        } else {
          this.isPinging = false
        }
        this.consecutiveMissedPings = 0
        return
      }

      if (this.isPinging) {
        return
      }

      this.isPinging = true
      const abortController = new AbortController()
      this.activePingAbortController = abortController

      try {
        await sendPing(pingPort, {
          signal: abortController.signal,
          timeoutMs: heartbeatTimeoutMs,
        })

        // Ping succeeded
        this.recordActivity()
      } catch (error) {
        if (!isCurrentWorker() || isExternalAbort(abortController.signal)) {
          return
        }

        // Check if system sleep / clock jump occurred while ping was in flight
        const nowAfterPing = Date.now()
        if (nowAfterPing - this.lastTickTime > heartbeatIntervalMs * 2.5) {
          log.warn('Sleep/throttle detected during ping. Discarding timeout.')
          this.consecutiveMissedPings = 0
          return
        }

        // 3. Activity awareness: if worker emitted events recently, it is alive and functional
        const timeSinceActivity = Date.now() - this.lastWorkerActivityTime
        if (timeSinceActivity < heartbeatTimeoutMs) {
          log.info(`Worker ping timed out but activity was observed ${timeSinceActivity}ms ago. Skipping crash.`)
          this.consecutiveMissedPings = 0
          return
        }

        // 4. Progressive confirmation before termination
        this.consecutiveMissedPings += 1
        if (this.consecutiveMissedPings < maxMissedPings) {
          log.warn(
            `Worker heartbeat missed (attempt ${this.consecutiveMissedPings}/${maxMissedPings}). Probing before termination...`
          )
          this.reporter.setSyncWarning('Sync connection is slow. Checking...')
          return
        }

        log.error(`Worker heartbeat failed after ${this.consecutiveMissedPings} missed pings:`, error)
        handleCrash(true)
      } finally {
        if (this.activePingAbortController === abortController) {
          this.activePingAbortController = null
          this.isPinging = false
        }
      }
    }, heartbeatIntervalMs)
  }
}
